'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import dynamic from 'next/dynamic';

const PromoSlideshow = dynamic(() => import('./PromoSlideshow'), {
  ssr: false,
});

type PromoBannerProps = {
  mainImage?: string;
  mainImageAlt: string;
  slideshowImages?: string[]; // Array of images for slideshow
  slideshowInterval?: number; // Optional: milliseconds between slides (default 3000)
  videoUrl?: string; // Video URL for video background
  videoPoster?: string; // Optional poster image for video
  youtubeUrl?: string; // YouTube video URL or embed ID
  useClickableThumbnail?: boolean; // If true, render thumbnail image linking to YouTube instead of iframe
  cornerImage: string;
  cornerImageAlt: string;
  link: string;
  linkText: string;
  header: string;
  subtext: string;
};

const PromoBanner: React.FC<PromoBannerProps> = ({
  mainImage,
  mainImageAlt,
  slideshowImages,
  slideshowInterval = 3000,
  videoUrl,
  videoPoster,
  youtubeUrl,
  useClickableThumbnail = false,
  cornerImage,
  cornerImageAlt,
  link,
  linkText,
  header,
  subtext,
}) => {
  // Extract YouTube video ID from URL if needed
  const getYouTubeEmbedUrl = (url: string) => {
    const videoIdMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/);
    const videoId = videoIdMatch ? videoIdMatch[1] : url;
    return `https://www.youtube.com/embed/${videoId}`;
  };

  // Determine which media type to use: youtube/thumbnail > video > slideshow > static image
  const useYouTubeEmbed = !!youtubeUrl && !useClickableThumbnail;
  const useYouTubeThumbnail = !!youtubeUrl && useClickableThumbnail;
  const useVideo = !youtubeUrl && !!videoUrl;
  const useSlideshow = !youtubeUrl && !useVideo && slideshowImages && slideshowImages.length > 0;
  const useStaticImage = !youtubeUrl && !useVideo && !useSlideshow;

  return (
    <section className="px-5 md:px-8 py-5 bg-black">
      <div className="max-w-[1440px] mx-auto relative">
        <div className="flex justify-start gap-10 flex-col md:flex-row">
          {/* Image/Video/YouTube block */}
          <div className={`w-full max-w-[1000px] rounded-[10px] overflow-hidden relative ${useYouTubeEmbed ? 'aspect-video' : 'h-[350px] md:h-[520px]'}`}>
          {useYouTubeEmbed ? (
            <iframe
              src={getYouTubeEmbedUrl(youtubeUrl)}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={mainImageAlt}
            />
          ) : useYouTubeThumbnail ? (
            <Link href={youtubeUrl} target="_blank" rel="noopener noreferrer">
              <Image
                src={mainImage!}
                alt={mainImageAlt}
                width={1000}
                height={520}
                className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                priority
              />
            </Link>
          ) : useVideo ? (
            <video
              autoPlay
              loop
              muted
              playsInline
              poster={videoPoster}
              className="w-full h-full object-cover"
              aria-label={mainImageAlt}
            >
              <source src={videoUrl} type="video/mp4" />
              {/* Fallback image if video fails to load */}
              {mainImage && (
                <Image
                  src={mainImage}
                  alt={mainImageAlt}
                  fill
                  className="object-cover"
                  priority
                />
              )}
            </video>
          ) : useSlideshow ? (
            <PromoSlideshow
              images={slideshowImages}
              interval={slideshowInterval}
              alt={mainImageAlt}
            />
          ) : (
            <Image
              src={mainImage!}
              alt={mainImageAlt}
              width={1000}
              height={520}
              className="w-full h-full object-cover"
              priority
            />
          )}

          {/* Mobile-only corner image - hide when using YouTube */}
          {!useYouTubeEmbed && !useYouTubeThumbnail && (
            <div className="w-[180px] h-[180px] absolute bottom-0 left-0 rounded-tr-2xl overflow-hidden block md:hidden">
              <Image
                src={cornerImage}
                alt={cornerImageAlt}
                width={180}
                height={180}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>

        {/* Text block */}
        <div className="w-full md:max-w-[370px] flex flex-col justify-center items-center text-white">
          <h2 className="text-[32px] md:text-[48px] font-semibold text-center mb-8 leading-tight">
            {header}
          </h2>

          <p className="text-[24px] md:text-[32px] font-extralight text-center mb-16 leading-snug">
            {subtext}
          </p>

          <Link href={link} target="_blank">
            <button className="bg-[#ED7523] hover:opacity-90 text-white px-6 py-3 rounded-[10px] font-medium text-[20px] leading-[22px] cursor-pointer transition-colors duration-300 max-w-[220px] w-full mx-auto">
              {linkText}
            </button>
          </Link>
        </div>
        </div>

        {/* Desktop-only corner image - hide when using YouTube */}
        {!useYouTubeEmbed && !useYouTubeThumbnail && (
          <div className="w-[277px] h-[277px] absolute bottom-0 left-0 rounded-tr-2xl overflow-hidden hidden md:block">
            <Image
              src={cornerImage}
              alt={cornerImageAlt}
              width={277}
              height={277}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default PromoBanner;
